/**
 * Fiş ekle (PDF §04, §06): kamera/galeri, birden fazla fotoğraf, otomatik okuma (OCR), taslak ve gönderim durumu.
 *
 * Fişteki bilgiler (tutar, tarih, saat, satıcı, gider kalemi) fotoğraftan otomatik dolar ve
 * kilitli gösterilir. Okunamayan alan boş kalırsa yalnızca o alan açılır; sistem emin olmadığı bilgiyi tahmin
 * etmez. El yazısı / okunaksız fişte hiçbir alan doldurulmaz, kullanıcı fotoğrafa bakarak girer (fotoğraf saklanır).
 * Okuma hatalıysa "Düzelt" ile alanlar açılabilir (kayıt yine sahibin onayıyla yapılır).
 * Fişte tarih okunamazsa (el yazısı, silik fiş) tarih ve saat fişin yüklendiği zamanla doldurulur.
 * Elle girilenler: açıklama ve ödeme şekli (kredi kartı, banka kartı, elden, veresiye). Veresiyede ödeme tarihi
 * girilir; vadeden 1 hafta, 3 gün önce ve vade günü herkese bildirim gider.
 * Taslak kimliği kamera açılmadan oluşturulur.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as ep from '../api/endpoints';
import { PAYMENT_LABELS, type Candidate, type OcrInfo, type PaymentMethod } from '../api/types';
import { useMe } from '../app/auth';
import { Icon } from '../components/Icon';
import { ErrorBox } from '../components/ui';
import { draftStore } from '../drafts/store';
import { addImage, applyServerCandidate, discardDraft, MAX_IMAGES, newDraft, saveFields, submitDraft, uploadDraft, uploadedAt as uploadTimeOf, validateFields } from '../drafts/sync';
import { emptyFields, type Draft, type DraftFields, type LocalState } from '../drafts/types';
import { formatDate, formatDateTime, isoToTR, todayTR } from '../lib/dates';
import { prepareImage } from '../lib/images';
import { formatTL, parseUserAmount } from '../lib/money';
import { clearPendingCapture, markPendingCapture, pendingCaptureDraftId } from '../platform/camera';

const STATE_LABEL: Record<LocalState, [string, string]> = {
  LOCAL_DRAFT: ['Taslak', ''],
  UPLOADING: ['Yükleniyor', 'info'],
  OCR_PENDING: ['Fiş okunuyor', 'info'],
  REVIEW_REQUIRED: ['İncelemede', 'warn'],
  READY: ['Kontrole hazır', 'ok'],
  SUBMITTING: ['Gönderiliyor', 'info'],
  POSTED: ['Kaydedildi', 'ok'],
  FAILED: ['Gönderilemedi', 'bad'],
};

type AutoKey = Exclude<keyof DraftFields, 'description' | 'payment_method' | 'payment_due_date'>;
const PAYMENT_ORDER: PaymentMethod[] = ['CREDIT_CARD', 'DEBIT_CARD', 'CASH', 'CREDIT_SALE'];
// okuma bundan uzun sürerse (sunucu yoğun / iş kuyruğu gecikmeli) alanlar elle girilebilir hale gelir
const OCR_WAIT_MS = 45_000;

export function AddReceiptScreen() {
  const me = useMe();
  const { draftId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [ocr, setOcr] = useState<OcrInfo | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);
  const [ocrSlow, setOcrSlow] = useState(false);
  // elle doldurulmaya başlanan alan yazarken açık kalır (ilk harften sonra kilitlenmez)
  const [typing, setTyping] = useState<Set<string>>(() => new Set());
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  // taslağı yükle / oluştur; kamera dönüşü kurtarma (T16)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const store = await draftStore();
      let d: Draft | undefined;
      const pending = pendingCaptureDraftId();
      const id = draftId ?? pending ?? undefined;
      if (id) d = await store.get(id);
      if (d && d.userId !== me.user.id) d = undefined; // başka hesabın taslağı görünmez
      if (!d) d = await newDraft(me.user.id);
      d = { ...d, fields: { ...emptyFields(), ...d.fields } }; // eski sürüm taslaklarında yeni alanlar yok
      d = recoverInterrupted(d);
      if (!cancelled) {
        setDraft(d);
        if (!draftId) nav(`/fis-ekle/${d.id}`, { replace: true });
      }
    })().catch(setError);
    return () => { cancelled = true; };
  }, [draftId, me.user.id, nav]);

  // önizlemeler
  useEffect(() => {
    if (!draft) return;
    let urls: string[] = [];
    void (async () => {
      const store = await draftStore();
      const next: Record<string, string> = {};
      for (const img of draft.images) {
        const blob = await store.getImage(draft.id, img.id);
        if (blob && !img.mime.includes('heic')) { const u = URL.createObjectURL(blob); urls.push(u); next[img.id] = u; }
      }
      setPreviews(next);
    })();
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); urls = []; };
  }, [draft?.id, draft?.images.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // OCR bitene kadar yokla (sunucu adayı)
  const candidate = useQuery({
    queryKey: ['candidate', draft?.candidateId],
    queryFn: () => ep.getCandidate(draft!.candidateId!),
    enabled: !!draft?.candidateId && (draft.state === 'OCR_PENDING' || draft.state === 'READY' || draft.state === 'REVIEW_REQUIRED'),
    // okuma sürerken saniyede bir; yönetici incelemesi beklenirken yarım dakikada bir
    refetchInterval: (q) => (q.state.data?.status === 'OCR_PENDING' ? 1000 : q.state.data?.status === 'REVIEW_REQUIRED' ? 30_000 : false),
  });
  useEffect(() => {
    const c = candidate.data;
    if (!c || !draft) return;
    setOcr(c.ocr);
    let next: Draft | null = null;
    if (c.status !== 'OCR_PENDING' && draft.state === 'OCR_PENDING') {
      next = applyServerCandidate(draft, c);
    } else if (draft.state === 'REVIEW_REQUIRED' && c.status !== 'REVIEW_REQUIRED') {
      next = afterReview(draft, c);   // yönetici karar verdi
    }
    if (next) {
      const saved = next;
      void draftStore().then((s) => s.put(saved));
      setDraft(saved);
    }
  }, [candidate.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (draft?.state !== 'OCR_PENDING') { setOcrSlow(false); return; }
    const t = window.setTimeout(() => setOcrSlow(true), OCR_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [draft?.state]);

  // alan değişikliği: kısa gecikmeyle kaydet; arka plana geçerken hemen kaydet
  const persist = useCallback((d: Draft) => { void draftStore().then((s) => s.put(d)); }, []);
  useEffect(() => {
    const flush = () => { if (document.visibilityState === 'hidden' && draft) persist(draft); };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => { document.removeEventListener('visibilitychange', flush); window.removeEventListener('pagehide', flush); };
  }, [draft, persist]);

  function setField(k: keyof DraftFields, v: string) {
    if (!draft) return;
    if (!typing.has(k)) setTyping((t) => new Set(t).add(k));
    const next = { ...draft, fields: { ...draft.fields, [k]: v } as DraftFields };
    setDraft(next);
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => persist(next), 400);
  }

  async function addFiles(files: File[]) {
    if (!draft) return;
    setError(null);
    setAdding(true);
    let d = draft;
    try {
      for (const f of files) {
        // telefonun verdiği biçim (WEBP, boş tür, çok büyük foto) sunucunun kabul ettiği JPEG/PNG/HEIC'e çevrilir
        const ready = await prepareImage(f);
        d = await addImage(d, ready.blob, ready.name);
      }
      setDraft(d);
      if (navigator.onLine) setDraft(await uploadDraft(d));
    } catch (e) { setError(e); setDraft(d); } finally { setAdding(false); }
  }

  async function retryUpload() {
    if (!draft) return;
    setError(null);
    setAdding(true);
    try { setDraft(await uploadDraft(draft)); } catch (e) { setError(e); } finally { setAdding(false); }
  }

  function capture(source: 'camera' | 'gallery') {
    if (!draft) return;
    markPendingCapture(draft.id); // kamera dönüşünde sayfa yeniden yüklenirse aynı taslağa dön
    (source === 'camera' ? cameraInput : fileInput).current?.click();
  }

  function onPicked(list: FileList | null) {
    const fs = [...(list ?? [])];
    clearPendingCapture();
    if (fs.length) void addFiles(fs);
  }

  async function submit() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await saveFields(draft, draft.fields);
      const d = await submitDraft(saved);
      setDraft(d);
      if (d.state === 'POSTED' && d.receiptId) {
        await Promise.all([qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['receipts'] }),
          qc.invalidateQueries({ queryKey: ['stats'] })]);
        await (await draftStore()).remove(d.id);
        nav(`/fis/${d.receiptId}`, { replace: true });
      }
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  if (!draft) return <ErrorBox error={error} />;
  const f = draft.fields;
  const errs = validateFields(f);
  const ambiguous = new Set(ocr?.fields?.ambiguous ?? []);
  const [stateLabel, stateCls] = STATE_LABEL[draft.state];
  const amountPreview = parseUserAmount(f.amount);
  const reading = adding || draft.state === 'UPLOADING' || (draft.state === 'OCR_PENDING' && !ocrSlow);
  const hasImages = draft.images.length > 0;
  const canSubmit = !busy && hasImages && Object.keys(errs).length === 0 && !reading
    && draft.state !== 'REVIEW_REQUIRED' && draft.state !== 'SUBMITTING';
  const notUploaded = draft.images.some((i) => !i.uploadedFileId);
  const unreadable = !reading && hasImages && (ocr?.fields?.readable === false || candidate.data?.last_error_code === 'OCR_UNREADABLE');
  const vendorMissing = hasImages && !reading && !f.vendor_name.trim();
  const todayIso = todayTR();
  // fişin sisteme yüklendiği an: ilk fotoğrafın sunucuya ulaştığı zaman (fişin saatinden ayrı tutulur)
  const uploadedAt = uploadTimeOf(candidate.data);
  // fişte tarih okunamadı, yüklenme günü/saati yazıldı
  const uploadTR = uploadedAt ? isoToTR(uploadedAt) : null;
  const dateFromUpload = !!uploadTR && !reading && !ocr?.fields?.document_date && f.document_date === uploadTR.date;

  /** Okunan alan: değer varsa kilitli gösterilir; okunamadıysa ya da "Düzelt" açıksa düzenlenir. */
  function row(k: AutoKey, label: string, show: (v: string) => ReactNode, input: ReactNode, extra = '') {
    const v = f[k];
    const open = editing || typing.has(k) || (!v && hasImages && !reading);
    return (
      <div className={`readout-row ${extra} ${open ? 'edit' : ''}`} key={k}>
        <span className="k">{label}{ambiguous.has(k) && !(dateFromUpload && (k === 'document_date' || k === 'document_time'))
          && <span className="badge warn" style={{ marginLeft: 6 }}>Kontrol edin</span>}
          {dateFromUpload && (k === 'document_date' || (k === 'document_time' && f.document_time === uploadTR?.time))
            && <span className="badge info" style={{ marginLeft: 6 }}>Yüklenme zamanı</span>}</span>
        {open ? <div style={{ flex: 1 }}>{input}</div>
          : <span className={`v ${!v ? 'missing' : ''}`}>{v ? show(v) : (reading ? <span className="reading" aria-label="Okunuyor" /> : '—')}</span>}
      </div>
    );
  }

  return (
    <div className="split">
      <div className="stack">
        <div className="page-head">
          <h1>Fiş ekle</h1>
          <span className={`badge ${stateCls}`}>{stateLabel}</span>
          <span className="spacer" />
          <Link to="/taslaklar" className="small">Taslaklarım</Link>
        </div>
        <ErrorBox error={error} />
        {draft.lastError && <div className={`alert ${draft.lastError.code === 'OFFLINE' ? 'warn' : 'bad'}`}>{draft.lastError.message}</div>}
        {draft.state === 'REVIEW_REQUIRED' && (
          <div className="alert warn">Benzer bir fiş bulundu. Yönetici incelemesi sonuçlanınca bildirim alacaksınız; kayıt toplamlara girmez.</div>
        )}

        <div className="capture">
          <button className="capture-btn primary" onClick={() => capture('camera')} disabled={draft.images.length >= MAX_IMAGES || adding}>
            <span className="cap-ico"><Icon name="camera" size={28} /></span>Fotoğraf çek<small>Arka kamera açılır</small>
          </button>
          <button className="capture-btn" onClick={() => capture('gallery')} disabled={draft.images.length >= MAX_IMAGES || adding}>
            <span className="cap-ico"><Icon name="image" size={28} /></span>Galeriden seç<small>Birden fazla seçilebilir</small>
          </button>
        </div>
        <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden
          onChange={(e) => { onPicked(e.target.files); e.target.value = ''; }} />
        <input ref={fileInput} type="file" accept="image/*,.heic,.heif" multiple hidden
          onChange={(e) => { onPicked(e.target.files); e.target.value = ''; }} />

        {hasImages && (
          <div className="card">
            <div className="thumbs">
              {draft.images.map((img) => (
                <div key={img.id} className="thumb" title={img.name}>
                  {previews[img.id]
                    ? <button type="button" className="thumb-open" onClick={() => setZoom(previews[img.id] ?? null)} aria-label="Fotoğrafı büyüt">
                        <img src={previews[img.id]} alt="Fiş" />
                      </button>
                    : <div className="thumb-file">{img.name}</div>}
                  <span className={`thumb-state ${img.uploadedFileId ? 'ok' : ''}`}>{img.uploadedFileId ? 'Yüklendi' : 'Cihazda'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="small muted" style={{ margin: '6px 4px 0' }}>En fazla {MAX_IMAGES} görsel. Fotoğraf ve POS slibi aynı giderin ekleri olarak eklenebilir.</p>
        {reading && hasImages && <div className="alert info">Fiş okunuyor… Bilgiler birkaç saniye içinde otomatik dolacak.</div>}
        {ocrSlow && draft.state === 'OCR_PENDING' && (
          <div className="alert warn">Otomatik okuma beklenenden uzun sürüyor. Bilgileri fotoğrafa bakarak elle girebilirsiniz;
            okuma biterse boş kalan alanlar yine dolar.</div>
        )}
        {ocr?.status === 'FAILED' && <div className="alert warn">Fiş otomatik okunamadı. Boş kalan alanları fotoğrafa bakarak doldurun.</div>}
        {unreadable && (
          <div className="alert warn">
            <strong>Fiş okunamadı (el yazısı ya da net değil).</strong> Fotoğrafa dokunup büyütün; tutar, satıcı ve gider
            kalemini elle doldurun. Tarih ve saat, fişin yüklendiği zamanla dolduruldu; fişteki tarih farklıysa “Düzelt” ile
            değiştirin. Fotoğraf fişle birlikte saklanır.
          </div>
        )}
        {hasImages && notUploaded && !reading && navigator.onLine && (
          <button type="button" className="btn" onClick={() => void retryUpload()}><Icon name="undo" size={16} />Yüklemeyi tekrar dene</button>
        )}
      </div>

      <div className="stack">
        <section className="readout" aria-label="Fişten okunan bilgiler">
          <div className="readout-head">
            <h2>Fişten okunan bilgiler</h2>
            <span className="auto-badge"><Icon name="sparkle" size={14} />Otomatik</span>
            <span className="spacer" />
            {hasImages && !reading && (
              <button type="button" className="btn small ghost" onClick={() => setEditing((e) => !e)}>
                <Icon name={editing ? 'check' : 'edit'} size={16} />{editing ? 'Bitti' : 'Düzelt'}
              </button>
            )}
          </div>
          {!hasImages ? (
            <div className="empty" style={{ paddingTop: 8 }}>Fişin fotoğrafını ekleyin; tutar, tarih, satıcı ve gider kalemi otomatik dolsun.</div>
          ) : (
            <>
              {row('amount', 'Tutar', (v) => { const k = parseUserAmount(v); return k ? formatTL(k) : v; },
                <input inputMode="decimal" placeholder="Tutarı yazın (ör. 125,50)" value={f.amount} onChange={(e) => setField('amount', e.target.value)} />, 'amount')}
              {row('document_date', 'Belge tarihi', (v) => formatDate(v),
                <input type="date" max={todayTR()} value={f.document_date} onChange={(e) => setField('document_date', e.target.value)} />)}
              {row('document_time', 'Saat', (v) => v,
                <input placeholder="Varsa (SS:DD)" value={f.document_time} onChange={(e) => setField('document_time', e.target.value)} />)}
              <div className="readout-row">
                <span className="k">Yüklenme zamanı</span>
                <span className={`v ${uploadedAt ? '' : 'missing'}`}>
                  {uploadedAt ? formatDateTime(uploadedAt) : (reading ? <span className="reading" aria-label="Yükleniyor" /> : '—')}
                </span>
              </div>
              {row('vendor_name', 'Satıcı', (v) => v,
                <input value={f.vendor_name} onChange={(e) => setField('vendor_name', e.target.value)} />)}
              {/* Vergi no ve fiş no ekranda gösterilmez; fişten okunabildiyse arka planda saklanır (mükerrer fiş kontrolü). */}
              {row('category', 'Gider kalemi', (v) => v,
                <CategoryInput value={f.category} vendor={f.vendor_name} onChange={(v) => setField('category', v)} />)}
            </>
          )}
          {ocr?.fields?.amount_candidates && ocr.fields.amount_candidates.length > 1 && (editing || ambiguous.has('amount')) && (
            <div className="small" style={{ padding: '0 16px 14px' }}>Okunan tutar adayları:{' '}
              {ocr.fields.amount_candidates.map((c) => (
                <button key={`${c.line}-${c.kurus}`} type="button" className="btn small" style={{ margin: 2 }} onClick={() => setField('amount', formatTL(BigInt(c.kurus), false))}>
                  {formatTL(BigInt(c.kurus))}
                </button>
              ))}
            </div>
          )}
          {vendorMissing && !unreadable && (
            <div className="alert warn small" style={{ margin: '0 16px 12px' }}>
              Satıcı adı fişten doğru okunamadı. Lütfen fotoğrafa bakarak <strong>satıcı adını elle yazın</strong>.
            </div>
          )}
          {editing && Object.entries(errs).filter(([k]) => !['description', 'payment_method', 'payment_due_date'].includes(k)).map(([k, m]) => (
            <div key={k} className="small" style={{ color: 'var(--bad)', padding: '0 16px 10px' }}>{m}</div>
          ))}
        </section>

        <div className="card stack" style={{ gap: 10 }}>
          <label className="field" style={{ margin: 0 }}>
            <span>Ödeme şekli</span>
            <select value={f.payment_method} onChange={(e) => setField('payment_method', e.target.value)}
              className={!f.payment_method && hasImages && !reading ? 'needs-input' : ''}>
              <option value="" disabled>Seçin…</option>
              {PAYMENT_ORDER.map((m) => <option key={m} value={m}>{PAYMENT_LABELS[m]}</option>)}
            </select>
          </label>
          {f.payment_method === 'CREDIT_SALE' && (
            <>
              <label className="field" style={{ margin: 0 }}>
                <span>Ödeme tarihi (vade)</span>
                <input type="date" value={f.payment_due_date} min={f.document_date || todayIso}
                  onChange={(e) => setField('payment_due_date', e.target.value)} />
              </label>
              <p className="small muted" style={{ margin: 0 }}>
                Ödeme gününden <strong>1 hafta önce</strong>, <strong>3 gün önce</strong> ve <strong>ödeme günü</strong> herkese
                hatırlatma bildirimi gider. Ödendiğinde fişin sayfasından ya da “Vadeli ödemeler”den “Ödendi” olarak işaretleyin.
              </p>
              {errs.payment_due_date && f.payment_due_date && <div className="small" style={{ color: 'var(--bad)' }}>{errs.payment_due_date}</div>}
            </>
          )}
        </div>

        <div className="card">
          <label className="field">
            <span>Açıklama</span>
            <textarea value={f.description} onChange={(e) => setField('description', e.target.value)} maxLength={500}
              placeholder="Bu harcama ne için yapıldı? (ör. saha aracı yakıtı, ekip öğle yemeği)" />
          </label>
        </div>

        <div className="alert info small">
          Gönder'e basarak <strong>bu harcamayı benim yaptığımı</strong> onaylıyorum. Fiş sahibi oturumunuzdan atanır; başkası adına fiş eklenemez.
          {amountPreview && <> Kaydedilecek tutar: <strong>{formatTL(amountPreview)}</strong>.</>}
        </div>
        {!hasImages && <p className="small muted" style={{ margin: 0 }}>Göndermek için en az bir fiş görseli ekleyin.</p>}
        {hasImages && !reading && Object.keys(errs).length > 0 && (
          <ul className="small muted todo-list">
            {Object.entries(errs).map(([k, m]) => <li key={k}>{m}</li>)}
          </ul>
        )}

        <div className="submit-bar">
          <button className="btn danger" onClick={() => { if (confirm('Taslak silinsin mi?')) void discardDraft(draft).then(() => nav('/taslaklar')); }}>Sil</button>
          <button className="btn primary" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? 'Gönderiliyor…' : 'Gönder ve kaydet'}
          </button>
        </div>
      </div>
      {zoom && (
        <div className="lightbox" role="dialog" aria-label="Fiş fotoğrafı" onClick={() => setZoom(null)}>
          <img src={zoom} alt="Fiş fotoğrafı" />
          <button type="button" className="btn small" onClick={() => setZoom(null)}><Icon name="close" size={16} />Kapat</button>
        </div>
      )}
    </div>
  );
}

/** Yönetici mükerrer incelemesini sonuçlandırınca taslak "İncelemede"den çıkar. */
function afterReview(d: Draft, c: Candidate): Draft {
  if (c.status === 'REJECTED' && c.last_error_code === 'DUPLICATE_CONFIRMED') {
    return { ...d, state: 'FAILED', lastError: { code: 'DUPLICATE_CONFIRMED',
      message: 'Yönetici bu fişi mükerrer (daha önce kaydedilmiş) olarak işaretledi; kaydedilmedi. Taslağı silebilirsiniz.' } };
  }
  const next = applyServerCandidate(d, c);
  if (c.last_error_code === 'NEED_BETTER_IMAGE') {
    return { ...next, lastError: { code: 'NEED_BETTER_IMAGE', message: 'Yönetici daha net bir fotoğraf istedi. Fişi yeniden çekip ekleyin, sonra gönderin.' } };
  }
  return { ...next, lastError: null };   // "ayrı fiş" kararı: gönderilebilir
}

/**
 * Uygulama yükleme ya da gönderme sırasında kapandıysa (kamera dönüşü, pil, ağ) taslak o durumda takılı kalmaz:
 * yüklemesi bitmişse okuma sonucu yeniden sorulur, bitmemişse "Yüklemeyi tekrar dene" görünür. Gönderim anahtarı
 * korunduğu için yeniden gönderme aynı fişi döndürür, ikinci kayıt oluşmaz.
 */
function recoverInterrupted(d: Draft): Draft {
  if (d.state === 'SUBMITTING') return { ...d, state: 'READY' };
  if (d.state !== 'UPLOADING') return d;
  const allUploaded = d.images.length > 0 && d.images.every((i) => i.uploadedFileId);
  return { ...d, state: d.candidateId ? (allUploaded ? 'OCR_PENDING' : 'READY') : 'LOCAL_DRAFT' };
}

/** Gider kalemi serbest metin; daha önce kullanılanlar önerilir. Satıcı biliniyorsa o satıcıda kullanılan kalem öne çıkar. */
function CategoryInput({ value, vendor, onChange }: { value: string; vendor: string; onChange: (v: string) => void }) {
  const q = useQuery({ queryKey: ['categories', value.slice(0, 2), vendor.trim().toLocaleLowerCase('tr-TR')],
    queryFn: () => ep.categorySuggest(value.slice(0, 2), vendor.trim()), staleTime: 60_000 });
  const fromVendor = q.data?.filter((c) => c.reason === 'vendor' && c.category !== value) ?? [];
  return (
    <>
      <input list="kategori-oneri" value={value} onChange={(e) => onChange(e.target.value)} maxLength={80} placeholder="ör. Yakıt, Yemek, Malzeme" />
      <datalist id="kategori-oneri">{q.data?.map((c) => <option key={c.category} value={c.category} />)}</datalist>
      {fromVendor.length > 0 && (
        <div className="small" style={{ marginTop: 4 }}>Bu satıcıda kullanılan:{' '}
          {fromVendor.map((c) => (
            <button key={c.category} type="button" className="btn small" style={{ margin: 2 }} onClick={() => onChange(c.category)}>{c.category}</button>
          ))}
        </div>
      )}
    </>
  );
}
